import { useState } from 'react';

const SA_ACCENT = '#6366F1';

interface ToggleRow {
  id: string;
  label: string;
  desc: string;
  on: boolean;
}

const initToggles: ToggleRow[] = [
  { id: 't1', label: 'Agency Self-Registration', desc: 'Allow new agencies to sign up from the public registration page', on: true },
  { id: 't2', label: '14-Day Trial Period', desc: 'Automatically grant new agencies a 14-day free trial', on: true },
  { id: 't3', label: 'Email Verification Required', desc: 'Agency owner email must be verified before access is granted', on: true },
  { id: 't4', label: 'Manual Approval Mode', desc: 'New agency registrations require Super Admin approval', on: false },
  { id: 't5', label: 'Global Maintenance Mode', desc: 'Take the entire platform offline for all agencies (emergency use)', on: false },
  { id: 't6', label: 'AI Assistant Platform-wide', desc: 'Allow all agencies on eligible plans to use the AI assistant', on: true },
  { id: 't7', label: 'WhatsApp Integration', desc: 'Enable WhatsApp integration for agencies on Professional+', on: true },
  { id: 't8', label: 'Platform Announcements Banner', desc: 'Show the announcement banner to all agency dashboards', on: false },
];

export default function SAPlatformSettings() {
  const [toggles, setToggles] = useState<ToggleRow[]>(initToggles);
  const [platformName, setPlatformName] = useState('TravelCRM Platform');
  const [platformEmail, setPlatformEmail] = useState('admin@travelcrm.com');
  const [supportEmail, setSupportEmail] = useState('support@travelcrm.com');
  const [trialDays, setTrialDays] = useState('14');
  const [smtpHost, setSmtpHost] = useState('smtp.sendgrid.net');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('apikey');
  const [announcement, setAnnouncement] = useState('');
  const [announcementType, setAnnouncementType] = useState<'info' | 'warning' | 'success'>('info');
  const [saved, setSaved] = useState<string | null>(null);

  const toggleSwitch = (id: string) => setToggles((prev) => prev.map((t) => t.id === id ? { ...t, on: !t.on } : t));

  const handleSave = (section: string) => {
    setSaved(section);
    setTimeout(() => setSaved(null), 2500);
  };

  return (
    <div className="space-y-5">
      {/* General Platform Settings */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <div>
          <h3 className="font-bold text-slate-800 text-sm">General Platform Settings</h3>
          <p className="text-xs text-slate-400 mt-0.5">Basic identity and configuration for the SaaS platform</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Platform Name</label>
            <input type="text" value={platformName} onChange={(e) => setPlatformName(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Admin Email</label>
            <input type="email" value={platformEmail} onChange={(e) => setPlatformEmail(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Support Email</label>
            <input type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Default Trial Period (days)</label>
            <input type="number" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} min="1"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Platform Base Currency</label>
            <select className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none cursor-pointer">
              <option>AED — UAE Dirham</option>
              <option>USD — US Dollar</option>
              <option>EUR — Euro</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-slate-50">
          {saved === 'general' && <span className="flex items-center gap-1.5 text-emerald-600 text-sm"><i className="ri-checkbox-circle-line" /> Settings saved!</span>}
          <div className="ml-auto">
            <button onClick={() => handleSave('general')} className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer" style={{ backgroundColor: SA_ACCENT }}>
              Save General Settings
            </button>
          </div>
        </div>
      </div>

      {/* SMTP Configuration */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
        <div>
          <h3 className="font-bold text-slate-800 text-sm">SMTP / Email Configuration</h3>
          <p className="text-xs text-slate-400 mt-0.5">Used to send system emails (invites, alerts, notifications)</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">SMTP Host</label>
            <input type="text" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">SMTP Port</label>
            <input type="text" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">SMTP Username</label>
            <input type="text" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">SMTP Password / API Key</label>
            <input type="password" placeholder="••••••••••••••••••••"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none" />
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-slate-50">
          {saved === 'smtp' && <span className="flex items-center gap-1.5 text-emerald-600 text-sm"><i className="ri-checkbox-circle-line" /> SMTP saved!</span>}
          <button className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 cursor-pointer">
            <i className="ri-mail-send-line mr-1" />
            Send Test Email
          </button>
          <button onClick={() => handleSave('smtp')} className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer" style={{ backgroundColor: SA_ACCENT }}>
            Save SMTP Config
          </button>
        </div>
      </div>

      {/* Announcement Banner */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
        <div>
          <h3 className="font-bold text-slate-800 text-sm">Global Announcement Banner</h3>
          <p className="text-xs text-slate-400 mt-0.5">Show a message to all agencies on their dashboard</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Banner Type</label>
          <div className="flex gap-2">
            {(['info', 'warning', 'success'] as const).map((t) => (
              <button key={t} onClick={() => setAnnouncementType(t)}
                className={`px-4 py-2 rounded-xl text-xs font-semibold cursor-pointer border-2 transition-colors capitalize ${
                  announcementType === t ? 'text-white border-transparent' :
                  t === 'info' ? 'border-sky-200 text-sky-600 bg-sky-50' :
                  t === 'warning' ? 'border-amber-200 text-amber-600 bg-amber-50' :
                  'border-emerald-200 text-emerald-600 bg-emerald-50'
                }`}
                style={announcementType === t ? {
                  backgroundColor: t === 'info' ? '#0EA5E9' : t === 'warning' ? '#F59E0B' : '#10B981'
                } : {}}>
                {t === 'info' ? '📢 Info' : t === 'warning' ? '⚠️ Warning' : '✅ Success'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Message (max 200 chars)</label>
          <textarea value={announcement} onChange={(e) => setAnnouncement(e.target.value.slice(0, 200))} rows={3}
            placeholder="e.g. We will be performing scheduled maintenance on Sunday March 30 from 2:00–4:00 AM UTC."
            className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none resize-none" />
          <p className="text-xs text-slate-400 mt-1">{announcement.length}/200 characters</p>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">Show banner:</span>
            <button onClick={() => toggleSwitch('t8')}
              className={`w-11 h-6 rounded-full relative transition-colors cursor-pointer ${toggles.find((t) => t.id === 't8')?.on ? '' : 'bg-slate-200'}`}
              style={toggles.find((t) => t.id === 't8')?.on ? { backgroundColor: SA_ACCENT } : {}}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${toggles.find((t) => t.id === 't8')?.on ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
            <span className="text-xs text-slate-500">{toggles.find((t) => t.id === 't8')?.on ? 'Visible' : 'Hidden'}</span>
          </div>
          <button onClick={() => handleSave('announcement')} className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer" style={{ backgroundColor: SA_ACCENT }}>
            {saved === 'announcement' ? '✓ Saved!' : 'Save Banner'}
          </button>
        </div>
      </div>

      {/* Feature Toggles */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="mb-5">
          <h3 className="font-bold text-slate-800 text-sm">Platform Feature Flags</h3>
          <p className="text-xs text-slate-400 mt-0.5">Enable or disable platform-wide behaviors and features</p>
        </div>
        <div className="space-y-1">
          {toggles.filter((t) => t.id !== 't8').map((t) => (
            <div key={t.id} className="flex items-center justify-between py-4 border-b border-slate-50 last:border-0">
              <div className="flex-1 pr-6">
                <p className="text-sm font-semibold text-slate-800">{t.label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{t.desc}</p>
              </div>
              <button onClick={() => toggleSwitch(t.id)}
                className={`w-11 h-6 rounded-full relative transition-colors cursor-pointer flex-shrink-0 ${t.on ? '' : 'bg-slate-200'}`}
                style={t.on ? { backgroundColor: t.id === 't5' ? '#EF4444' : SA_ACCENT } : {}}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${t.on ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={() => handleSave('flags')} className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer" style={{ backgroundColor: SA_ACCENT }}>
            {saved === 'flags' ? '✓ Saved!' : 'Save Feature Flags'}
          </button>
        </div>
      </div>
    </div>
  );
}